<template>
  <v-overlay v-if="!termConditionsChecked">
    <v-progress-circular indeterminate size="64"> </v-progress-circular>
  </v-overlay>
  <v-app v-else id="inspire">
    <v-app-bar app color="main" dark>
      <v-app-bar-nav-icon @click.stop="drawer = !drawer" class="mx-auto" />
        <router-link :to="{ path: '/'}">
          <v-img v-if="tenantsWithLogos.includes(`${$auth.tenant}`)"
            max-height="110"  
            max-width="110"
            class="ml-6"
            :src="require(`@/assets/${$auth.tenant}_logo.png`)" 
            :alt="`${$auth.tenant} Logo`"> 
          </v-img>
          <v-app-bar-nav-icon v-else>
            <v-icon color="#fff">mdi-home</v-icon>
          </v-app-bar-nav-icon>
        </router-link>
      <v-spacer />
      <v-col cols="auto" class="px-2">
        <AdminViewModeToggle />
      </v-col>
      <v-col cols="auto" class="pr-0">
        <PortalNavMenu active="tpr" />
      </v-col>
    </v-app-bar>
    <v-navigation-drawer v-model="drawer" app>
      <v-list nav>
        <v-list-item :to="{ path: '/' }" exact link>
          <v-list-item-action>
            <v-icon>mdi-note-text-outline</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>TPR Manager</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="isTprProUser" :to="{ path: '/tpr-pro'}">
          <v-list-item-action>
            <v-icon>mdi-account-supervisor</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>TPR Pro</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="showExport20Nav" :to="{ path: '/export-20' }" exact link>
          <v-list-item-action>
            <v-icon>mdi-file-export</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>Active Future TPR Pivot</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
        <v-list-item v-if="showAdminResyncNav" :to="{ path: '/admin-resync' }" exact link>
          <v-list-item-action>
            <v-icon>mdi-sync</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>Admin Batch Resync</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <template v-slot:append>
        <v-divider></v-divider>
        <div :class="[!miniVariant ? 'pa-2' : 'pa-1']">
          <v-btn
            block
            id="logoutBtn"
            :style="styleObject"
            @click.prevent="logout()"
          >
            <v-icon color="#fff">mdi-logout</v-icon>
            <span v-if="!miniVariant">Logout</span>
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <v-main>
      <!-- <AlertBar></AlertBar> -->
      <router-view v-if="hasPortalAccess" />
      <v-alert v-else type="error" text border="left">
        You don't have authorized permissions to access this site.
      </v-alert>
      <v-hover v-slot="{ hover }">
        <v-snackbar
          top
          right
          rounded="md"
          min-height="50"
          content-class="body-1"
          transition="slide-y-transition"
          v-model="snackbar.showing"
          :timeout="snackbar.timeout"
          :color="snackbar.color"
        >
          <v-btn v-if="snackbar.status === 'error'" icon @click="closeSnackbar">
            <v-icon v-if="!hover">{{ snackbar.icon }}</v-icon>
            <v-icon v-else>{{ snackbar.closeIcon }}</v-icon>
          </v-btn>
          <v-icon v-else left>
            {{ snackbar.icon }}
          </v-icon>
          {{ snackbar.text }}
        </v-snackbar>
      </v-hover>
    </v-main>
    <v-footer color="main" app class="justify-space-between">
      <span class="white--text">
        iPro
        <span class="registeredTrademark">&reg;</span> Systems, LLC. &copy;
        {{ new Date().getFullYear() }}
      </span>
      <span class="blue--text text--lighten-2">v. {{ version }}</span>
    </v-footer>
    <TermsAndConditionsModal
      v-if="showTermsAndConditionsModal"
      v-model="showTermsAndConditionsModal"
      @update="updateTC"
      @logout="logout">
    </TermsAndConditionsModal>
  </v-app>
</template>

<script>
import PartyAttributeValue from '@/axios/party-attribute-value-endpoint'
import Party from '@/axios/parties'
import PortalNavMenu from "@/components/PortalNavMenu";
import TermsAndConditionsModal from '@/components/TermsAndConditionsModal.vue'
import AdminViewModeToggle from "@/components/AdminViewModeToggle.vue";
import { userAccess } from "@/mixins/user-access";
import { displayAlert } from '@/mixins/alert'
import { mapState } from "vuex";

export default {
  name: "App",
  components: { PortalNavMenu, TermsAndConditionsModal, AdminViewModeToggle },
  mixins: [userAccess, displayAlert],
  data() {
    return {
      drawer: false,
      miniVariant: false,
      styleObject: {
        background: "#E53935",
        color: "white",
        fontSize: "15px",
      },
      showTermsAndConditionsModal: false,
      termConditionsChecked: false
    };
  },
  computed: {
    ...mapState(["snackbar"]),
    version() {
      return process.env.VUE_APP_VERSION;
    },
    tenantsWithLogos () {
      return [        
        'awg',
        'alliance-retail-group'      
      ]  
    },
    isTprProUser() {
      return this.$auth.tenant === 'alliance-retail-group' && 
             ['tpr-manager.pro', 'tpr-manager.admin', 'tpr-manager.coordinator'].some(role => this.userGroups.includes(role));
    },
    showExport20Nav() {
        // Only show Export 20 to ad group level users (not TPR Pro retail users)
        return this.$auth.tenant == 'alliance-retail-group' && ['tpr-manager.admin', 'arg-admin', 'arg-category-manager', 'arg-group-manager', 'arg-support'].some(role => this.userGroups.includes(role));
    },
    showAdminResyncNav() {
      // Only show Admin Batch Resync to ARG Users with Admin Role
      return this.$auth.tenant === 'alliance-retail-group' && this.userGroups.includes('tpr-manager.admin')
    }
  },
  watch: {
    tenant: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.$store.dispatch("getAdGroups");
          this.$store.dispatch("getUserRelationships");
          if (newValue === 'alliance-retail-group') {
            this.$store.dispatch('getUserTprProStores');
          }
        }
      },
    },
    '$auth.isLoading': {
      immediate: true,
      async handler (isLoading) {
        if (isLoading === false && this.$auth.isAuthenticated) {
          this.checkTermConditions()
        }
      }
    }
  },
  created() {
    this.$store.dispatch('initViewMode');
    if (this.$auth?.tenant) {
      this.$store.dispatch("getUserRelationships");
      if (this.$auth.tenant === 'alliance-retail-group') {
        this.$store.dispatch('getUserTprProStores');
      }
    }
  },
  methods: {
    logout() {
      sessionStorage.removeItem("tpr_manager_ad_group");
      sessionStorage.removeItem("tpr_manager_selected_batch");
      sessionStorage.removeItem("tpr_manager_tpr_date");
      sessionStorage.removeItem("tpr_manager_item_search");
      sessionStorage.removeItem("tpr_manager_view_mode");
      this.$auth.logout({
        returnTo: `${window.location.origin}`,
      });
    },
    closeSnackbar () {
      this.snackbar.showing = false
    },
    async checkTermConditions() {
      
      if (this.$auth.tenant === 'alliance-retail-group' && this.isARGRetailer) {
        this.termConditionsPA = null
        try {
          const email = this.$store.getters.email
          
          const customerIdRes = await Party.getCustomerId(email)
          
          if (customerIdRes?.data?.customer_id) {
            const res = await PartyAttributeValue.getAttributeValuesByParty(customerIdRes.data.customer_id, 100, 0);
            if (res?.data?.length > 0) {
              this.termConditionsPA = res.data.find(pa => pa.attr.id === "TERMS_CONDITIONS_ACCEPTED")
            }
            
            if (!this.termConditionsPA || this.termConditionsPA?.value?.toLowerCase() !== 'true') {
              this.showTermsAndConditionsModal = true
            }
            this.termConditionsChecked = true
          } else {
            console.log('No customer_id found, setting termConditionsChecked to true')
            this.termConditionsChecked = true
          }
        } catch (err) {
          console.error('Error checking T&C:', err)
          this.termConditionsChecked = true
          if (err.response) {
            console.error('Error response:', err.response.status, err.response.data)
          }
        }
      } else {
        this.termConditionsChecked = true
      }
    },
    async updateTC() {
      let saveData = null
      const customerIdRes = await Party.getCustomerId(this.$store.getters.email)
      if (this.termConditionsPA) {
        saveData = {
          ...this.termConditionsPA,
          value: 'True'
        }
      } else {
        saveData = {
          attr_id: "TERMS_CONDITIONS_ACCEPTED",
          created_by: this.$store.getters.email,
          party_id: customerIdRes.data.customer_id,
          tenant: 'alliance-retail-group',
          created_on: new Date().toISOString(),
          updated_on: new Date().toISOString(),
          value: 'True'
        }
      }
      await this.handleAttributeValue(saveData)
    },
    async handleAttributeValue (saveData) {
      if (saveData.id && saveData.attr) {
        const saveId = saveData.id
        saveData.attr_id = saveData.attr.id
        saveData.updated_by = this.$store.getters.email
        delete saveData.attr
        delete saveData.id
        await PartyAttributeValue.put(saveId, saveData).then(() => {
          this.emitAlert(true, 'success', 'Your acceptance has been recorded.', [], true)
          this.showTermsAndConditionsModal = false
        }).catch(err => {
          this.handleError(err)
        })
      } else {
        await PartyAttributeValue.post(saveData).then(() => {
          this.emitAlert(true, 'success', 'Your acceptance has been recorded.', [], true)
          this.showTermsAndConditionsModal = false
        }).catch(err => {
          this.handleError(err)
        })
      }
    }
  },
};
</script>
<style scoped>
span.registeredTrademark {
  position: relative;
  top: -0.3rem;
  font-size: 0.5rem;
  left: -0.05rem;
}
</style>
